import {
  CloseOutlined,
  PauseOutlined,
  CaretRightOutlined,
} from '@ant-design/icons';
import { useRef } from 'react';

import {
  formatLeadAudioTime,
  scrollToLeadMessage,
} from '@app/components/LeadCard/ui/MessageHistory/lib';
import {
  LEAD_AUDIO_SPEEDS,
  leadAudioPlayback,
  useLeadAudioPlayback,
} from '@app/components/LeadCard/ui/MessageHistory/model/leadAudioPlayback';

import type { FC, PointerEvent as ReactPointerEvent, RefObject } from 'react';

import './LeadPinnedAudioPlayer.scss';

interface LeadPinnedAudioPlayerProps {
  scrollContainerRef: RefObject<HTMLDivElement | null>;
}

export const LeadPinnedAudioPlayer: FC<LeadPinnedAudioPlayerProps> = ({
  scrollContainerRef,
}) => {
  const playback = useLeadAudioPlayback();
  const progressRef = useRef<HTMLDivElement | null>(null);
  const isDraggingRef = useRef(false);

  // Закреп живёт, пока есть выбранная запись: после её окончания остаётся с
  // крестиком, чтобы можно было переслушать (решение Михаила 23.08).
  if (playback.fileId === null) return null;

  const progress =
    playback.durationSec > 0
      ? Math.min(1, playback.positionSec / playback.durationSec)
      : 0;

  const seekByPointer = (clientX: number) => {
    const node = progressRef.current;
    if (!node) return;

    const rect = node.getBoundingClientRect();
    if (!rect.width) return;

    leadAudioPlayback.seekRatio((clientX - rect.left) / rect.width);
  };

  const pointerDownHandler = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    isDraggingRef.current = true;
    seekByPointer(event.clientX);
  };

  const pointerMoveHandler = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (isDraggingRef.current) seekByPointer(event.clientX);
  };

  const pointerUpHandler = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    isDraggingRef.current = false;
  };

  const playClickHandler = () => {
    if (playback.isPlaying) {
      leadAudioPlayback.pause();

      return;
    }

    void leadAudioPlayback.resume();
  };

  const speedClickHandler = () => {
    const index = LEAD_AUDIO_SPEEDS.indexOf(playback.speed);
    const next = LEAD_AUDIO_SPEEDS[(index + 1) % LEAD_AUDIO_SPEEDS.length];

    leadAudioPlayback.setSpeed(next);
  };

  const scrollToMessageHandler = () => {
    if (playback.messageId === null) return;

    scrollToLeadMessage(scrollContainerRef.current, playback.messageId);
  };

  return (
    <div
      className="LeadPinnedAudioPlayer"
      role="button"
      tabIndex={0}
      title="Перейти к сообщению"
      onClick={scrollToMessageHandler}
      onKeyDown={(event) => {
        // Внутри плашки свои кнопки: Enter/Space на «Паузе» или смене скорости
        // не должны заодно уводить историю к сообщению. Клик они гасят сами,
        // клавиатура всплывает отдельно.
        if (event.target !== event.currentTarget) return;
        if (event.key === 'Enter' || event.key === ' ')
          scrollToMessageHandler();
      }}
    >
      <button
        type="button"
        className="LeadPinnedAudioPlayer__play"
        aria-label={playback.isPlaying ? 'Пауза' : 'Воспроизвести'}
        onClick={(event) => {
          event.stopPropagation();
          playClickHandler();
        }}
      >
        {playback.isPlaying ? <PauseOutlined /> : <CaretRightOutlined />}
      </button>
      <div className="LeadPinnedAudioPlayer__body">
        <span className="LeadPinnedAudioPlayer__title">{playback.title}</span>
        <div
          ref={progressRef}
          className="LeadPinnedAudioPlayer__progress"
          // Вся плашка — кнопка «Перейти к сообщению». Гашения pointerdown
          // мало: click всплывает отдельным событием, и перемотка уводила бы
          // историю к сообщению.
          onClick={(event) => event.stopPropagation()}
          onPointerDown={pointerDownHandler}
          onPointerMove={pointerMoveHandler}
          onPointerUp={pointerUpHandler}
          onPointerCancel={pointerUpHandler}
        >
          <span
            className="LeadPinnedAudioPlayer__progress-value"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      </div>
      <span className="LeadPinnedAudioPlayer__time">
        {formatLeadAudioTime(playback.positionSec)} /{' '}
        {formatLeadAudioTime(playback.durationSec)}
      </span>
      <button
        type="button"
        className="LeadPinnedAudioPlayer__speed"
        aria-label="Скорость воспроизведения"
        onClick={(event) => {
          event.stopPropagation();
          speedClickHandler();
        }}
      >
        {playback.speed}×
      </button>
      <button
        type="button"
        className="LeadPinnedAudioPlayer__close"
        aria-label="Закрыть плеер"
        onClick={(event) => {
          event.stopPropagation();
          leadAudioPlayback.stop();
        }}
      >
        <CloseOutlined />
      </button>
    </div>
  );
};
